<template>
  <div class="tool-page">
    <Navigation />

    <h1>{{ $t('aiActImage.title') }}</h1>
    <p class="intro">{{ $t('aiActImage.intro') }}</p>
    <p class="disclaimer">
      {{ $t('aiActImage.disclaimer') }}
      <a
        href="https://digital-strategy.ec.europa.eu/de/policies/eu-icons-labelling-ai-generated-content"
        target="_blank"
        rel="noopener noreferrer"
      >{{ $t('aiActImage.euLink') }}</a>
    </p>
    <p class="local-note">{{ $t('aiActImage.localOnly') }}</p>

    <section class="tool-card">
      <h2>{{ $t('aiActImage.uploadTitle') }}</h2>
      <div
        class="dropzone"
        :class="{ active: isDragging }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="onDrop"
        @click="$refs.fileInput.click()"
      >
        <p>{{ $t('aiActImage.dropHint') }}</p>
        <p class="hint">{{ $t('aiActImage.uploadLimits') }}</p>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          multiple
          hidden
          @change="onFileSelect"
        >
      </div>

      <ul v-if="files.length" class="file-list">
        <li v-for="(item, index) in files" :key="item.id">
          <span>{{ item.file.name }}</span>
          <span class="meta">{{ formatBytes(item.file.size) }}</span>
          <button type="button" class="link-btn" @click="removeFile(index)">{{ $t('aiActImage.remove') }}</button>
        </li>
      </ul>
      <button v-if="files.length" type="button" class="secondary-btn" @click="clearFiles">
        {{ $t('aiActImage.clearAll') }}
      </button>
    </section>

    <section class="tool-card">
      <h2>{{ $t('aiActImage.settingsTitle') }}</h2>

      <div class="settings-grid">
        <label>
          <span>{{ $t('aiActImage.labelType') }}</span>
          <select v-model="settings.type">
            <option value="generated">{{ $t('aiActImage.typeGenerated') }}</option>
            <option value="modified">{{ $t('aiActImage.typeModified') }}</option>
            <option value="basic">{{ $t('aiActImage.typeBasic') }}</option>
          </select>
        </label>

        <label>
          <span>{{ $t('aiActImage.labelColor') }}</span>
          <select v-model="settings.color">
            <option value="white">{{ $t('aiActImage.colorWhite') }}</option>
            <option value="black">{{ $t('aiActImage.colorBlack') }}</option>
            <option value="white-50">{{ $t('aiActImage.colorWhite50') }}</option>
            <option value="black-50">{{ $t('aiActImage.colorBlack50') }}</option>
          </select>
        </label>

        <label>
          <span>{{ $t('aiActImage.position') }}</span>
          <select v-model="settings.position">
            <option value="bottom-right">{{ $t('aiActImage.posBottomRight') }}</option>
            <option value="bottom-left">{{ $t('aiActImage.posBottomLeft') }}</option>
            <option value="top-right">{{ $t('aiActImage.posTopRight') }}</option>
            <option value="top-left">{{ $t('aiActImage.posTopLeft') }}</option>
          </select>
        </label>

        <label>
          <span>{{ $t('aiActImage.maxSide') }}</span>
          <input
            v-model="settings.maxSide"
            type="number"
            min="1"
            step="1"
            :placeholder="$t('aiActImage.maxSidePlaceholder')"
          >
          <small>{{ $t('aiActImage.maxSideHint') }}</small>
        </label>
      </div>

      <div class="size-block">
        <span class="block-label">{{ $t('aiActImage.labelSize') }}</span>
        <div class="presets">
          <button
            type="button"
            :class="{ active: settings.sizePercent === 8 }"
            @click="settings.sizePercent = 8"
          >{{ $t('aiActImage.sizeSmall') }}</button>
          <button
            type="button"
            :class="{ active: settings.sizePercent === 12 }"
            @click="settings.sizePercent = 12"
          >{{ $t('aiActImage.sizeMedium') }}</button>
          <button
            type="button"
            :class="{ active: settings.sizePercent === 18 }"
            @click="settings.sizePercent = 18"
          >{{ $t('aiActImage.sizeLarge') }}</button>
        </div>
        <div class="slider-row">
          <input
            v-model.number="settings.sizePercent"
            type="range"
            min="5"
            max="100"
            step="1"
          >
          <strong>{{ settings.sizePercent }}%</strong>
        </div>
        <p v-if="previewLabelPx" class="hint">{{ $t('aiActImage.approxPx', { px: previewLabelPx }) }}</p>
      </div>

      <div v-if="previewUrl" class="preview-block">
        <h3>{{ $t('aiActImage.previewTitle') }}</h3>
        <img :src="previewUrl" :alt="$t('aiActImage.previewTitle')" class="preview-image">
      </div>
    </section>

    <section class="tool-card actions-card">
      <button
        type="button"
        class="primary-btn"
        :disabled="!files.length || isRunning"
        @click="startConvert"
      >
        {{ $t('aiActImage.convert') }}
      </button>

      <div v-if="isRunning || progress.total > 0" class="progress-panel">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <p class="progress-status">
          {{ progress.statusText }} — {{ progressPercent }}%
          <span v-if="etaText"> · {{ etaText }}</span>
        </p>
        <div class="progress-controls">
          <button type="button" class="secondary-btn" :disabled="!isRunning" @click="togglePause">
            {{ isPaused ? $t('aiActImage.resume') : $t('aiActImage.pause') }}
          </button>
          <button type="button" class="secondary-btn" :disabled="!isRunning" @click="cancelConvert">
            {{ $t('aiActImage.cancel') }}
          </button>
        </div>
      </div>
    </section>

    <section v-if="results.length" class="tool-card">
      <div class="results-header">
        <h2>{{ $t('aiActImage.resultsTitle') }}</h2>
        <button type="button" class="primary-btn" @click="downloadAllZip">
          {{ $t('aiActImage.downloadAll') }}
        </button>
      </div>
      <ul class="results-list">
        <li v-for="item in results" :key="item.id">
          <div
            class="thumb-wrap"
            @mouseenter="showHoverPreview($event, item)"
            @mousemove="moveHoverPreview($event)"
            @mouseleave="hideHoverPreview"
          >
            <img :src="item.url" :alt="item.fileName" class="thumb">
          </div>
          <div class="result-meta">
            <strong>{{ item.fileName }}</strong>
            <span>{{ item.width }}×{{ item.height }} px</span>
          </div>
          <button type="button" class="secondary-btn" @click="downloadOne(item)">
            {{ $t('aiActImage.downloadOne') }}
          </button>
        </li>
      </ul>
    </section>

    <Teleport to="body">
      <div
        v-if="hoverPreview.visible"
        class="result-hover-preview"
        :style="{ top: hoverPreview.y + 'px', left: hoverPreview.x + 'px' }"
      >
        <img :src="hoverPreview.url" :alt="hoverPreview.fileName">
        <span>{{ hoverPreview.fileName }}</span>
      </div>
    </Teleport>

    <LegalFooter />
  </div>
</template>

<script>
import JSZip from 'jszip'
import Navigation from '../sections/Navigation.vue'
import LegalFooter from '../legal/LegalFooter.vue'
import { labelFile, revokeObjectUrl } from '../../utils/aiActImageLabel'

const MAX_FILES = 20
const MAX_FILE_BYTES = 10 * 1024 * 1024

export default {
  name: 'AIActImageConverter',
  components: { Navigation, LegalFooter },
  data() {
    return {
      files: [],
      results: [],
      isDragging: false,
      isRunning: false,
      isPaused: false,
      cancelRequested: false,
      previewUrl: '',
      previewLabelPx: null,
      previewTimer: null,
      hoverPreview: {
        visible: false,
        url: '',
        fileName: '',
        x: 0,
        y: 0
      },
      settings: {
        type: 'generated',
        color: 'white',
        position: 'bottom-right',
        sizePercent: 12,
        maxSide: ''
      },
      progress: {
        current: 0,
        total: 0,
        statusText: '',
        startedAt: 0
      }
    }
  },
  computed: {
    progressPercent() {
      if (!this.progress.total) return 0
      return Math.min(100, Math.round((this.progress.current / this.progress.total) * 100))
    },
    etaText() {
      if (!this.isRunning || this.progress.current === 0) return ''
      const elapsed = Date.now() - this.progress.startedAt
      const perItem = elapsed / this.progress.current
      const remaining = Math.max(0, this.progress.total - this.progress.current)
      const ms = Math.round(perItem * remaining)
      const seconds = Math.ceil(ms / 1000)
      return this.$t('aiActImage.eta', { seconds })
    },
    convertOptions() {
      const maxSideRaw = String(this.settings.maxSide || '').trim()
      const maxSide = maxSideRaw === '' ? null : Number(maxSideRaw)
      return {
        type: this.settings.type,
        color: this.settings.color,
        position: this.settings.position,
        sizePercent: this.settings.sizePercent,
        maxSide: Number.isFinite(maxSide) && maxSide > 0 ? maxSide : null
      }
    }
  },
  watch: {
    settings: {
      deep: true,
      handler() {
        this.schedulePreview()
      }
    },
    files() {
      this.schedulePreview()
    }
  },
  beforeUnmount() {
    this.clearPreview()
    this.clearResults()
    this.hideHoverPreview()
    if (this.previewTimer) clearTimeout(this.previewTimer)
  },
  methods: {
    showHoverPreview(event, item) {
      this.hoverPreview = {
        visible: true,
        url: item.url,
        fileName: item.fileName,
        x: 0,
        y: 0
      }
      this.moveHoverPreview(event)
    },
    moveHoverPreview(event) {
      if (!this.hoverPreview.visible) return
      const offset = 18
      const previewW = 360
      const previewH = 360
      let x = event.clientX + offset
      let y = event.clientY + offset
      if (x + previewW > window.innerWidth - 12) {
        x = event.clientX - previewW - offset
      }
      if (y + previewH > window.innerHeight - 12) {
        y = event.clientY - previewH - offset
      }
      this.hoverPreview.x = Math.max(8, x)
      this.hoverPreview.y = Math.max(8, y)
    },
    hideHoverPreview() {
      this.hoverPreview.visible = false
      this.hoverPreview.url = ''
      this.hoverPreview.fileName = ''
    },
    formatBytes(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    onDrop(event) {
      this.isDragging = false
      this.addFiles(Array.from(event.dataTransfer.files || []))
    },
    onFileSelect(event) {
      this.addFiles(Array.from(event.target.files || []))
      event.target.value = ''
    },
    addFiles(list) {
      const images = list.filter((f) => f.type.startsWith('image/'))
      for (const file of images) {
        if (this.files.length >= MAX_FILES) {
          alert(this.$t('aiActImage.tooManyFiles', { max: MAX_FILES }))
          break
        }
        if (file.size > MAX_FILE_BYTES) {
          alert(this.$t('aiActImage.fileTooLarge', { name: file.name }))
          continue
        }
        this.files.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file
        })
      }
    },
    removeFile(index) {
      this.files.splice(index, 1)
    },
    clearFiles() {
      this.files = []
      this.clearPreview()
    },
    clearPreview() {
      revokeObjectUrl(this.previewUrl)
      this.previewUrl = ''
      this.previewLabelPx = null
    },
    clearResults() {
      this.hideHoverPreview()
      this.results.forEach((item) => revokeObjectUrl(item.url))
      this.results = []
    },
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(() => this.refreshPreview(), 250)
    },
    async refreshPreview() {
      if (!this.files.length) {
        this.clearPreview()
        return
      }
      try {
        const result = await labelFile(this.files[0].file, this.convertOptions)
        revokeObjectUrl(this.previewUrl)
        this.previewUrl = URL.createObjectURL(result.blob)
        this.previewLabelPx = result.labelPx
      } catch (error) {
        console.error(error)
      }
    },
    async waitWhilePaused() {
      while (this.isPaused && this.isRunning && !this.cancelRequested) {
        await new Promise((r) => setTimeout(r, 120))
      }
    },
    async startConvert() {
      if (!this.files.length || this.isRunning) return
      this.clearResults()
      this.isRunning = true
      this.isPaused = false
      this.cancelRequested = false
      this.progress = {
        current: 0,
        total: this.files.length,
        statusText: this.$t('aiActImage.statusStarting'),
        startedAt: Date.now()
      }

      const nextResults = []
      try {
        for (let i = 0; i < this.files.length; i += 1) {
          if (this.cancelRequested) break
          await this.waitWhilePaused()
          if (this.cancelRequested) break

          const item = this.files[i]
          this.progress.statusText = this.$t('aiActImage.statusProcessing', { name: item.file.name })
          const labelled = await labelFile(item.file, this.convertOptions)
          nextResults.push({
            id: item.id,
            fileName: labelled.fileName,
            blob: labelled.blob,
            url: URL.createObjectURL(labelled.blob),
            width: labelled.width,
            height: labelled.height
          })
          this.progress.current = i + 1
        }
        this.results = nextResults
        this.progress.statusText = this.cancelRequested
          ? this.$t('aiActImage.statusCancelled')
          : this.$t('aiActImage.statusDone')
      } catch (error) {
        console.error(error)
        this.progress.statusText = this.$t('aiActImage.statusError')
        alert(this.$t('aiActImage.convertError'))
      } finally {
        this.isRunning = false
        this.isPaused = false
      }
    },
    togglePause() {
      if (!this.isRunning) return
      this.isPaused = !this.isPaused
      this.progress.statusText = this.isPaused
        ? this.$t('aiActImage.statusPaused')
        : this.$t('aiActImage.statusProcessing', { name: '…' })
    },
    cancelConvert() {
      this.cancelRequested = true
      this.isPaused = false
    },
    downloadOne(item) {
      const a = document.createElement('a')
      a.href = item.url
      a.download = item.fileName
      a.click()
    },
    async downloadAllZip() {
      if (!this.results.length) return
      const zip = new JSZip()
      this.results.forEach((item) => {
        zip.file(item.fileName, item.blob)
      })
      const blob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'ai-act-labelled-images.zip'
      a.click()
      setTimeout(() => revokeObjectUrl(url), 1000)
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 120px 20px 40px;
  color: #e2e8f0;
  min-height: 100vh;
}

h1 {
  font-size: 2.4rem;
  margin-bottom: 16px;
  color: #fff;
  text-align: center;
  font-weight: 700;
}

.intro,
.disclaimer,
.local-note {
  line-height: 1.6;
  margin: 8px 0;
  text-align: center;
}

.disclaimer a {
  color: #00ffb3;
}

.local-note {
  opacity: 0.85;
  font-size: 0.95rem;
  margin-bottom: 28px;
}

.tool-card {
  background: rgba(15, 16, 35, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 28px;
  margin-bottom: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.35);
}

h2 {
  color: #fff;
  font-size: 1.35rem;
  margin: 0 0 16px;
  border-bottom: 2px solid #00ffb3;
  padding-bottom: 8px;
}

h3 {
  color: #fff;
  font-size: 1.1rem;
  margin: 18px 0 10px;
}

.dropzone {
  border: 2px dashed rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  padding: 28px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.dropzone.active,
.dropzone:hover {
  border-color: #00ffb3;
  background: rgba(0, 255, 179, 0.06);
}

.hint {
  font-size: 0.85rem;
  opacity: 0.75;
  margin-top: 8px;
}

.file-list,
.results-list {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
}

.file-list li,
.results-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-wrap: wrap;
}

.meta {
  opacity: 0.7;
  font-size: 0.85rem;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.92rem;
}

select,
input[type='number'],
input[type='range'] {
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
}

small {
  opacity: 0.7;
  font-size: 0.8rem;
  line-height: 1.4;
}

.size-block {
  margin-top: 20px;
}

.block-label {
  display: block;
  margin-bottom: 8px;
}

.presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.presets button {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
}

.presets button.active {
  border-color: #00ffb3;
  background: rgba(0, 255, 179, 0.15);
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.slider-row input {
  flex: 1;
}

.preview-image {
  max-width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.primary-btn,
.secondary-btn,
.link-btn {
  cursor: pointer;
}

.primary-btn {
  background: linear-gradient(135deg, rgba(0, 255, 179, 0.25), rgba(139, 92, 246, 0.35));
  border: 1px solid rgba(0, 255, 179, 0.45);
  color: #fff;
  border-radius: 999px;
  padding: 12px 22px;
  font-weight: 600;
}

.primary-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.secondary-btn {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  border-radius: 999px;
  padding: 8px 14px;
}

.link-btn {
  background: none;
  border: none;
  color: #00ffb3;
  text-decoration: underline;
  padding: 0;
}

.progress-panel {
  margin-top: 18px;
}

.progress-bar {
  height: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #00ffb3;
  transition: width 0.2s ease;
}

.progress-status {
  margin: 10px 0;
  font-size: 0.92rem;
}

.progress-controls {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.results-header h2 {
  margin: 0;
  border: none;
  padding: 0;
}

.thumb-wrap {
  position: relative;
  flex-shrink: 0;
}

.thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  cursor: zoom-in;
}

.result-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 140px;
}

@media (max-width: 768px) {
  .tool-page {
    padding-top: 100px;
  }

  h1 {
    font-size: 1.9rem;
  }

  .tool-card {
    padding: 20px 16px;
  }
}
</style>

<style>
.result-hover-preview {
  position: fixed;
  z-index: 10000;
  pointer-events: none;
  max-width: min(360px, 70vw);
  max-height: min(360px, 70vh);
  padding: 10px;
  border-radius: 12px;
  background: rgba(10, 12, 28, 0.96);
  border: 1px solid rgba(0, 255, 179, 0.35);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-hover-preview img {
  display: block;
  max-width: 100%;
  max-height: min(300px, 60vh);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
}

.result-hover-preview span {
  color: #e2e8f0;
  font-size: 0.8rem;
  word-break: break-all;
  opacity: 0.85;
}
</style>
